import { type EnrollmentStatusFilters } from 'components/EmployeesTable'
import { employersEmployees } from 'rails_routes'
import { cn } from 'utils'
import { type EmployersEnrollmentStatusCounts } from 'utils/web_api.schemas'

import {
  type DisplayedEnrollmentStatus,
  ENROLLMENT_STATUS_DISPLAY_DICT,
  ENROLLMENT_STATUS_ORDER,
  ENROLLMENT_STATUS_STYLE
} from './EnrollmentTracker'

type Props = {
  enrollmentStatusCounts: EmployersEnrollmentStatusCounts | undefined,
  hoveredStatus: DisplayedEnrollmentStatus | null,
  setHoveredStatus: (status: DisplayedEnrollmentStatus | null) => void,
  isLoading: boolean,
}

// Maps displayed enrollment statuses to the actual filter values used in EmployeesTable
const STATUS_TO_EMPLOYEE_FILTERS: Record<DisplayedEnrollmentStatus, (keyof EnrollmentStatusFilters)[]> = {
  not_invited: ['not_invited'],
  enrolled: ['enrolled'],
  viewing_plans: ['in_progress'],
  invite_sent: ['invited', 'invite_pending', 'pending_automated_invite'],
  declined: ['declined', 'expired'],
}

const buildEmployeesUrlWithFilters = (status: DisplayedEnrollmentStatus): string => {
  const filters = STATUS_TO_EMPLOYEE_FILTERS[status]
  const params = new URLSearchParams()
  filters.forEach((filter) => params.append('enrollment_status', filter.toString()))
  return `${employersEmployees.index()}?${params.toString()}`
}

export default function EnrollmentsLegendItems (props: Props) {
  const {
    enrollmentStatusCounts,
    hoveredStatus,
    setHoveredStatus,
    isLoading,
  } = props

  return (
    <div className='flex flex-col'>
      {ENROLLMENT_STATUS_ORDER.map((key) => {
        const isHovered = hoveredStatus === key
        return (
          <div
            key={key}
            className='flex items-center gap-2 h-8 bg-gray-50 cursor-pointer'
            onMouseEnter={() => setHoveredStatus(key)}
            onMouseLeave={() => setHoveredStatus(null)}
          >
            <div className='flex items-center gap-2 flex-1'>
              <div
                style={ENROLLMENT_STATUS_STYLE[key]}
                className={cn('w-2 h-2 rounded-full transition-transform duration-150 shrink-0', {
                  'scale-110': isHovered,
                })}
              />
              <a
                className={cn('text-gray-500 hover:text-gray-500 hover:underline transition-all duration-150', {
                  'body-sm-bold': isHovered,
                  'body-sm': !isHovered,
                })}
                href={buildEmployeesUrlWithFilters(key)}
              >
                {ENROLLMENT_STATUS_DISPLAY_DICT[key]}
              </a>
              <span className='flex-1 border-b border-dotted border-gray-300' />
            </div>
            {isLoading
              ? <div className='w-4 h-4 rounded-md bg-gray-200 animate-pulse' />
              : (
                <p className={cn('body-sm-bold text-gray-800 transition-all duration-150', { 'scale-105': isHovered })}>
                  {enrollmentStatusCounts?.[key] || 0}
                </p>
                )}
          </div>
        )
      })}
    </div>
  )
}
